import debounce from "lodash/debounce"
import { useEffect } from "react"

// Uncomment debug styles for .is-targeted-in-hash to use.
function debug({
  selector,
  entry
}: {
  selector: string
  entry: IntersectionObserverEntry
}) {
  const klass = "is-targeted-in-hash"

  // Remove debug class.
  const targetedElements = Array.from(
    document.querySelectorAll(`${selector}.${klass}`)
  )
  targetedElements.forEach((targetedElement) =>
    targetedElement.classList.remove(klass)
  )

  // Add a (temporary) class to the element targeted by the hash
  if (!entry.target.classList.contains(klass)) {
    entry.target.classList.add(klass)
  }
}

type UseHashObserverOptions = {
  selector: string
  isActive: boolean
}

const useHashObserver = (
  { selector, isActive }: UseHashObserverOptions = {
    isActive: true
  } as UseHashObserverOptions
) => {
  useEffect(() => {
    // Don't setup observer if not active.
    if (!isActive) return

    // Escape any forward slashes (not valid selector).
    selector = selector.replace(/\//g, "\\/")

    // Store an intersection entry per element. Since the observer callback
    // is called initially for each observed target, we get an initial record
    // of all entries which is helpful since elements don't always intersect in
    // order of appearance.
    const entriesMap: Map<Element, IntersectionObserverEntry> = new Map([])

    // Debounce updating the location hash, since calling replaceState
    // too frequently can result in warnings/errors depending on the browser.
    const updateHash = debounce(
      (hash: string) => {
        // Update the URL hash with the target element ID
        if (window.location.hash !== hash) {
          window.history.replaceState(null, "", hash)
        }
      },
      100,
      { leading: true }
    )

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        entriesMap.set(entry.target, entry)
      })

      entries = []
      entriesMap.forEach((entry) => entries.push(entry))

      // Find the closest intersecting entry.
      let entry = entries.sort(
        (entryA, entryB) =>
          // Entry with highest intersection ratio is most visible (1 being full visible).
          entryB.intersectionRatio - entryA.intersectionRatio ||
          // If a tie, entry closest to top is more visible.
          entryA.boundingClientRect.top - entryB.boundingClientRect.top
      )[0]

      // HACK: it may be that the provided selector no longer contains the enry
      // we found. In an edge case, when used in Step content, the hook may still
      // be running on an inactive Step since we continue to render Steps adjecent
      // to the active Step for better UX (in a way).
      // @ts-ignore
      const node = [...document.querySelectorAll(selector)].find(
        (node) => node === entry.target
      )

      const elementId = node?.id

      if (elementId) {
        updateHash(`#${elementId}`)
        // debug({ selector, entry })
      }
    }

    // Debounced callback
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: [1, 0.75, 0.5, 0.25]
    })

    // Observe all elements matching provided selector.
    document
      .querySelectorAll(selector)
      .forEach((child) => observer.observe(child))

    // Cleanup
    return () => {
      observer.disconnect()
      // Clear the hash.
      if (window.location.hash) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search
        )
      }
    }
  }, [selector, isActive])
}

export default useHashObserver
